import type { HealthDataConsentRead } from '$lib/api/generated/schemas';

/**
 * SML-140: without valid health-data consent every content endpoint answers
 * 403 `HEALTH_CONSENT_REQUIRED`. The server decides access; the client drops
 * the tenant content it still holds, refreshes the current user so the layout
 * guards can route to the consent screen, and does not keep retrying a request
 * that cannot succeed.
 */
export const HEALTH_CONSENT_REQUIRED = 'HEALTH_CONSENT_REQUIRED';

const DEFAULT_QUERY_RETRIES = 3;

/** The mutator throws `{ code, status }` with an allow-listed (sanitized) code. */
export function isHealthConsentRequiredError(error: unknown): boolean {
	return (
		typeof error === 'object' &&
		error !== null &&
		(error as { code?: unknown }).code === HEALTH_CONSENT_REQUIRED
	);
}

export interface HealthConsentLossActions {
	/** Consent state of the cached `GET /api/users/me` (`undefined` = not loaded). */
	cachedConsent: () => HealthDataConsentRead | null | undefined;
	/** Drop cached content, stores and drafts, and start a new session epoch. */
	resetTenantContent: () => void;
	/** Re-read `GET /api/users/me` so the guards see the consent state. */
	refreshCurrentUser: () => void;
}

/**
 * Global query/mutation `onError`. A consent-required failure while the client
 * still believes consent is granted means it was withdrawn elsewhere (another
 * tab or device): the content this tab holds is dropped once. The reset also
 * drops the cached user, so further 403s of the same burst — and the 403s of
 * the remounted pages — only refresh the user and can never reset in a loop.
 * Returns whether it reacted, for tests.
 */
export function handleHealthConsentRequiredError(
	error: unknown,
	actions: HealthConsentLossActions
): boolean {
	if (!isHealthConsentRequiredError(error)) return false;
	if (actions.cachedConsent()?.status === 'granted') actions.resetTenantContent();
	actions.refreshCurrentUser();
	return true;
}

/**
 * A `GET /api/users/me` update that turns a granted consent into any other
 * known state: consent was withdrawn elsewhere and this tab learned it from the
 * user itself, before (or without) any 403. `undefined` (not loaded, or
 * dropped by a reset) never counts, so the reset cannot repeat itself.
 */
export function isConsentLoss(
	previous: HealthDataConsentRead['status'] | undefined,
	next: HealthDataConsentRead['status'] | undefined
): boolean {
	return previous === 'granted' && next !== undefined && next !== 'granted';
}

/** Default query `retry`: same budget as before (3), but never for missing consent. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
	if (isHealthConsentRequiredError(error)) return false;
	return failureCount < DEFAULT_QUERY_RETRIES;
}
